import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Printer, Save, Share2, X } from "lucide-react";

export type BookingStatus = "pending" | "confirmed" | "completed" | "cancelled";

export const SERVICE_OPTIONS = ["Transfert", "Excursion", "Mise à disposition", "Circuit"] as const;

export interface BookingDetail {
  id: string;
  order_number: number | null;
  created_at: string;
  name: string;
  phone: string;
  email: string | null;
  service: string;
  pickup_location: string | null;
  dropoff_location: string | null;
  booking_date: string | null;
  message: string | null;
  status: BookingStatus;
  driver_name: string | null;
  driver_phone: string | null;
  transfer_price: number | null;
  passengers: number | null;
}

const STATUS_LABEL: Record<BookingStatus, string> = {
  pending: "En attente",
  confirmed: "Confirmé",
  completed: "Terminé",
  cancelled: "Annulé",
};

export const formatOrderNumber = (n: number | null) =>
  n == null ? "—" : `MT-${String(n).padStart(6, "0")}`;


const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

const esc = (v: string) =>
  v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const imageToDataUrl = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Logo indisponible");
  const blob = await response.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Logo invalide"));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
};

const BookingDetailDialog = ({
  booking,
  requirePrice = false,
  onClose,
  onSaved,
}: {
  booking: BookingDetail | null;
  requirePrice?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [price, setPrice] = useState("");
  const [service, setService] = useState("");
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [passengers, setPassengers] = useState("");
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    setDriverName(booking?.driver_name || "");
    setDriverPhone(booking?.driver_phone || "");
    setPrice(booking?.transfer_price != null ? String(booking.transfer_price) : "");
    setService(booking?.service || "");
    setPickup(booking?.pickup_location || "");
    setDropoff(booking?.dropoff_location || "");
    setPassengers(booking?.passengers != null ? String(booking.passengers) : "");
  }, [booking?.id, booking?.passengers, booking?.driver_name, booking?.driver_phone, booking?.transfer_price, booking?.service, booking?.pickup_location, booking?.dropoff_location]);


  if (!booking) return null;

  const save = async () => {
    const trimmed = price.trim();
    const parsed = trimmed === "" ? null : Number(trimmed.replace(",", "."));
    if (trimmed !== "" && (parsed == null || Number.isNaN(parsed) || parsed < 0)) {
      toast.error("Prix invalide — saisissez un montant en MAD (ex. 450).");
      return;
    }
    if (requirePrice && parsed == null) {
      toast.error("Le prix du transfert est obligatoire pour confirmer cette réservation.");
      return;
    }
    const paxTrim = passengers.trim();
    const paxParsed = paxTrim === "" ? null : Number(paxTrim);
    if (paxTrim !== "" && (!Number.isInteger(paxParsed) || (paxParsed as number) < 1)) {
      toast.error("Nombre de passagers invalide — saisissez un nombre entier (ex. 4).");
      return;
    }
    setSaving(true);
    const update = {
      driver_name: driverName || null,
      driver_phone: driverPhone || null,
      transfer_price: parsed,
      service: service || booking.service,
      pickup_location: pickup.trim() || null,
      dropoff_location: dropoff.trim() || null,
      passengers: paxParsed,
      ...(requirePrice && parsed != null ? { status: "confirmed" } : {}),
    };

    const { error } = await supabase.from("bookings").update(update).eq("id", booking.id);

    setSaving(false);
    if (error) toast.error("Erreur: " + error.message);
    else {
      toast.success(requirePrice ? "Prix enregistré — réservation confirmée" : "Réservation enregistrée");
      onSaved();
    }
  };

  const print = () => {
    const rows: [string, string][] = [
      ["Numéro d'ordre", formatOrderNumber(booking.order_number)],
      ["Statut", STATUS_LABEL[booking.status]],
      ["Date souhaitée du transfert", booking.booking_date ? fmtDate(booking.booking_date) : "—"],
      ["Client", booking.name],
      ["Téléphone", booking.phone],
      ["E-mail", booking.email || "—"],
      ["Service", service || booking.service],
      ["Lieu de départ", pickup.trim() || "—"],
      ["Lieu d'arrivée", dropoff.trim() || "—"],
      ...(passengers.trim() !== "" ? ([["Nombre de passagers", passengers.trim()]] as [string, string][]) : []),

      ["Prix du transfert", price.trim() !== "" ? `${price.trim()} MAD` : "—"],
      ["Message", booking.message || "—"],
      ["Chauffeur", driverName || "—"],
      ["Téléphone chauffeur", driverPhone || "—"],
    ];

    const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8">
<title>${esc(formatOrderNumber(booking.order_number))} - Your Booking</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: Georgia, "Times New Roman", serif; color: #000; background: #fff; margin: 0; }
  .sheet { width: 174mm; margin: 0 auto; }
  .head { text-align: center; border-bottom: 3px double #000; padding-bottom: 12px; }
  .head img { height: 54px; }
  .brand { margin-top: 5px; font-size: 24px; font-weight: bold; letter-spacing: 3px; }
  h1 { text-align: center; font-size: 22px; margin: 26px 0 6px; letter-spacing: 1px; }
  .order { text-align: center; font-size: 15px; margin-bottom: 22px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #000; padding: 8px 10px; font-size: 13px; text-align: left; vertical-align: top; }
  th { width: 38%; background: #f2f2f2; }
  .foot { margin-top: 28px; border-top: 1px solid #000; padding-top: 10px; font-size: 11px; text-align: center; }
</style></head><body><main class="sheet">
<div class="head">
  <img src="${window.location.origin}/logo.png" alt="Maroctransfers" />
  <div class="brand">MAROCTRANSFERS</div>
</div>
<h1>Your Booking</h1>
<div class="order"><strong>${esc(formatOrderNumber(booking.order_number))}</strong> &nbsp;•&nbsp; Imprimé le ${esc(new Date().toLocaleDateString("fr-FR"))}</div>
<table>${rows.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join("")}</table>
<div class="foot">Maroctransfers.com — Merci</div>
</main>
<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
</body></html>`;

    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) {
      toast.error("Autorisez les fenêtres pop-up pour imprimer.");
      return;
    }
    w.document.write(html);
    w.document.close();
  };

  const createPdf = async () => {
    const [{ jsPDF }, { default: autoTable }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ]);
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();

    try {
      const logo = await imageToDataUrl(`${window.location.origin}/logo.png`);
      doc.addImage(logo, "PNG", (pageWidth - 18) / 2, 14, 18, 18);
    } catch {
      // Le document reste partageable si le logo ne peut pas être chargé.
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("MAROCTRANSFERS", pageWidth / 2, 39, { align: "center" });
    doc.setLineWidth(0.5);
    doc.line(18, 44, pageWidth - 18, 44);
    doc.setFontSize(16);
    doc.text("Your Booking", pageWidth / 2, 54, { align: "center" });
    doc.setFontSize(11);
    doc.text(formatOrderNumber(booking.order_number), pageWidth / 2, 61, { align: "center" });

    const rows: [string, string][] = [
      ["Numéro d'ordre", formatOrderNumber(booking.order_number)],
      ["Statut", STATUS_LABEL[booking.status]],
      ["Date souhaitée du transfert", booking.booking_date ? fmtDate(booking.booking_date) : "—"],
      ["Client", booking.name],
      ["Téléphone", booking.phone],
      ["E-mail", booking.email || "—"],
      ["Service", service || booking.service],
      ["Lieu de départ", pickup.trim() || "—"],
      ["Lieu d'arrivée", dropoff.trim() || "—"],
      ...(passengers.trim() !== "" ? ([["Nombre de passagers", passengers.trim()]] as [string, string][]) : []),
      ["Prix du transfert", price.trim() !== "" ? `${price.trim()} MAD` : "—"],
      ["Message", booking.message || "—"],
      ["Chauffeur", driverName || "—"],
      ["Téléphone chauffeur", driverPhone || "—"],
    ];

    autoTable(doc, {
      startY: 68,
      margin: { left: 18, right: 18 },
      body: rows,
      theme: "grid",
      styles: { font: "helvetica", fontSize: 9, cellPadding: 3, textColor: 0, lineColor: 0, lineWidth: 0.2 },
      columnStyles: { 0: { fontStyle: "bold", fillColor: [242, 242, 242], cellWidth: 62 } },
    });

    const finalY = (doc as typeof doc & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 245;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Maroctransfers.com — Merci", pageWidth / 2, Math.min(finalY + 14, 282), { align: "center" });
    return doc.output("blob");
  };

  const sharePdf = async () => {
    setSharing(true);
    try {
      const blob = await createPdf();
      const fileName = `Maroctransfers-${formatOrderNumber(booking.order_number)}.pdf`;
      const file = new File([blob], fileName, { type: "application/pdf" });
      const shareData = { files: [file], title: `Réservation ${formatOrderNumber(booking.order_number)}` };

      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      toast.info("PDF téléchargé. Vous pouvez maintenant le joindre dans WhatsApp.");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      toast.error("Impossible de créer le PDF. Veuillez réessayer.");
    } finally {
      setSharing(false);
    }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Réservation {formatOrderNumber(booking.order_number)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-border p-4 text-center">
            <div className="text-xl font-heading tracking-widest">MAROCTRANSFERS</div>
            <div className="text-xs text-muted-foreground">maroctransfers.com</div>
            <div className="mt-3 font-heading text-lg">Your Booking</div>
            <div className="text-sm text-muted-foreground">
              {formatOrderNumber(booking.order_number)} • {STATUS_LABEL[booking.status]}
            </div>
          </div>

          <dl className="grid grid-cols-3 gap-y-2 text-sm font-body">
            {(
              [
                ["Date de la demande", fmtDate(booking.created_at)],
                ["Date souhaitée", booking.booking_date ? fmtDate(booking.booking_date) : "—"],
                ["Client", booking.name],
                ["Téléphone", booking.phone],
                ["E-mail", booking.email || "—"],
                ["Message", booking.message || "—"],
              ] as [string, string][]
            ).map(([k, v]) => (
              <div key={k} className="col-span-3 grid grid-cols-3 gap-2">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="col-span-2 break-words">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="rounded-lg border border-border p-4 space-y-3">
            <p className="text-sm font-medium">Trajet</p>
            <div className="space-y-1.5">
              <Label>Service</Label>
              <Select value={SERVICE_OPTIONS.includes(service as typeof SERVICE_OPTIONS[number]) ? service : ""} onValueChange={setService}>
                <SelectTrigger>
                  <SelectValue placeholder={service || "Choisir un service"} />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pickup-location">Lieu de départ</Label>
              <Input id="pickup-location" value={pickup} onChange={(e) => setPickup(e.target.value)} placeholder="Ex. Aéroport Marrakech" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dropoff-location">Lieu d'arrivée</Label>
              <Input id="dropoff-location" value={dropoff} onChange={(e) => setDropoff(e.target.value)} placeholder="Ex. Essaouira" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="passengers">Nombre de passagers (facultatif)</Label>
              <Input
                id="passengers"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={passengers}
                onChange={(e) => setPassengers(e.target.value)}
                placeholder="Ex. 4"
              />
            </div>
          </div>


          <div className="rounded-lg border border-border p-4 space-y-3">
            <p className="text-sm font-medium">Tarification</p>
            {requirePrice && (
              <p className="text-xs text-muted-foreground">
                Saisissez le prix du transfert pour confirmer cette réservation.
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="transfer-price">Prix du transfert (MAD)</Label>
              <Input
                id="transfer-price"
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Ex. 450"
              />
            </div>
          </div>

          <div className="rounded-lg border border-border p-4 space-y-3">
            <p className="text-sm font-medium">Chauffeur</p>
            <div className="space-y-1.5">
              <Label htmlFor="driver-name">Nom du chauffeur</Label>
              <Input id="driver-name" value={driverName} onChange={(e) => setDriverName(e.target.value)} placeholder="Ex. Youssef" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="driver-phone">Numéro du chauffeur</Label>
              <Input id="driver-phone" value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} placeholder="Ex. +212 6 00 00 00 00" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-2" /> Fermer
            </Button>
            <Button variant="secondary" onClick={save} disabled={saving}>
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
            <Button variant="outline" onClick={sharePdf} disabled={sharing}>
              <Share2 className="w-4 h-4 mr-2" /> {sharing ? "Préparation…" : "Envoyer par WhatsApp"}
            </Button>
            <Button onClick={print}>
              <Printer className="w-4 h-4 mr-2" /> Imprimer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDetailDialog;
